import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LinkTypes } from '@modules/card/enums/link-types.enum';
import { TargetType } from '@modules/card/enums/target-type.enum';
import { CardLinkInterface } from '@modules/card/interfaces/card-link.interface';
import { TARGET_NEW_SPECT } from '@modules/card/interfaces/card.interface';
import { FavorableContent } from '@modules/favorites/interfaces/favorable-content.interface';
import { MediaImageModel } from '@modules/media/models/media-image.model';
import { MediaVideoModel } from '@modules/media/models/media-video.model';
import { Action } from '@modules/shared/action-button-group/action.types';
import { TrackableContent } from '@modules/shared/services/data-layer.service';
import { DownloadableContent } from '@modules/shared/services/download.service';
import { SharableContent } from '@modules/shared/services/share.service';
import { MetaItemInterface } from '@modules/ui/meta-items/meta-items.component';

import { AssetType } from '../types/asset.types';

type TagConfiguration = { icon: string; label: string; assetCountLabel?: string };

@Component({
  selector: 'mb-media-card',
  templateUrl: './media-card.component.html',
  styleUrls: ['./media-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MediaCardComponent implements OnInit {
  @Input() data!: FavorableContent & DownloadableContent & SharableContent & TrackableContent;
  @Input() actions?: Action[];
  @Input() assetType: AssetType = 'PRESS_INFORMATION';
  @Input() assetCount?: number;
  @Input() darkTheme: boolean = true;
  @Input() embargoDates?: string[];
  @Input() fuelLabel?: string;
  @Input() headline!: string;
  @Input() link: CardLinkInterface | null = null;
  @Input() media?: MediaVideoModel | MediaImageModel;
  @Input() metaItems?: MetaItemInterface[];
  @Input() text?: string;
  @Input() topline?: string;
  @Input() toplineIcon?: string;
  @Input() videoDuration?: number;
  @Input() public showImageFallback = false;

  public linkTypes = LinkTypes;
  public tag?: TagConfiguration;
  public videoDurationText: string = '';

  private assetTypeTagConfiguration: { [k in AssetType]?: TagConfiguration } = {
    DOCUMENT: {
      icon: 'bds/document/24',
      label: 'DOCUMENT',
      assetCountLabel: 'PAGES_N',
    },
    TECHNICAL_DATA: {
      icon: 'bds/document/24',
      label: 'TECHNICAL_DATA',
      assetCountLabel: 'PAGES_N',
    },
    IMAGE: {
      icon: 'bds/camera/24',
      label: 'IMAGE',
    },
    MEDIA_COLLECTION: {
      icon: 'bds/grid-view-4/16',
      label: 'MEDIA_COLLECTION',
      assetCountLabel: 'TYPE_N',
    },
    MEDIA_SPECIAL: {
      icon: 'bds/star/16',
      label: 'MEDIA_SPECIAL',
    },
    PRESS_INFORMATION: {
      icon: 'bds/layers/16',
      label: 'PRESS_INFORMATION',
    },
    PRESS_KIT: {
      icon: 'bds/layers/16',
      label: 'PRESS_KIT',
    },
    VIDEO: {
      icon: 'bds/media-play/16',
      label: 'VIDEO',
    },
    AUDIO: {
      icon: 'bds/media-play/16',
      label: 'AUDIO',
    },
  };

  constructor(
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.tag = this.assetTypeTagConfiguration[this.assetType];
  }

  public linkAction(e: MouseEvent) {
    const { target, query, type, url, outlets } = this.link as CardLinkInterface;
    const programmaticRouting = type === this.linkTypes.PROGRAMMATIC;

    if (target !== TargetType.BLANK && !programmaticRouting && !query) {
      return;
    }
    e.preventDefault();
    if (programmaticRouting || query) {
      this.router
        .navigate([], {
          relativeTo: this.route,
          queryParams: {
            q: query || '',
          },
          queryParamsHandling: 'merge',
        })
        .then(() => {
          this.router.navigate([url], outlets);
        });
      return false;
    }

    window.open.apply(null, [url, '_blank', TARGET_NEW_SPECT]);
    return false;
  }
}
